import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Skeleton,
  Space,
  Table,
  Typography,
  message,
} from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { QueryOperation } from "../../code/search-query/search-query";
import { nameOf } from "../../utils/typescript";
import {
  addCompanyDepartment,
  getCompanyDepartments,
  updateCompanyDepartment,
} from "../../repository/data.repository";
import {
  Company,
  Department,
  MAX_PAGE_SIZE,
} from "../../repository/data.default";
import { retry, getProblemMessage } from "../../utils/http";
import ComboBox from "../../controls/ComboBox";
import { useSelector } from "../../store/store";
import { authorizeSelectors } from "../../store/authorize";

export interface CompanyDepartmentsPanelProps {
  readonly open: boolean;
  readonly companyUid: string | undefined;
  readonly companyName?: string;
  readonly reloadToken?: string | number;
  readonly onChange?: () => void;
  readonly onCountLoaded?: (count: number) => void;
  readonly scopeCompanies: Company[];
}

function CompanyDepartmentsPanel(props: CompanyDepartmentsPanelProps) {
  const permissions = useSelector(authorizeSelectors.permissions);
  const canCreate = permissions.can("CompanyDepartments", "create");
  const canEdit = permissions.can("CompanyDepartments", "edit");

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<Department[]>([]);
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Department | null>(null);
  const everLoadedRef = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scopeUids = useMemo(
    () => props.scopeCompanies.map((c) => c.uid),
    [props.scopeCompanies],
  );
  const scopeKey = scopeUids.join("|");

  useEffect(() => {
    if (!props.open || scopeUids.length === 0) return;
    load(search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, scopeKey, props.reloadToken]);

  useEffect(() => {
    if (!props.open || scopeUids.length === 0) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(search), 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  function load(nameFilter: string): void {
    if (scopeUids.length === 0) return;
    setLoading(true);
    const conditions: any[] = [
      {
        field: nameOf<Department>("companyUid"),
        values: scopeUids,
        operation: QueryOperation.In,
      },
    ];
    if (nameFilter.trim()) {
      conditions.push({
        field: nameOf<Department>("name"),
        values: [nameFilter.trim()],
        operation: QueryOperation.StartsWith,
      });
    }
    retry(() =>
      getCompanyDepartments({
        searchQuery: { conditions },
        pageNumber: 0,
        pageSize: MAX_PAGE_SIZE,
      }),
    )
      .then(({ data }) => {
        setItems(data);
        setLoading(false);
        everLoadedRef.current = true;
        props.onCountLoaded?.(data.length);
      })
      .catch(() => setLoading(false));
  }

  function onMutated(): void {
    setAddOpen(false);
    setEditTarget(null);
    load(search);
    props.onChange?.();
  }

  if (!props.open) return null;

  const showSkeleton = loading && !everLoadedRef.current;
  const multiCompany = props.scopeCompanies.length > 1;

  return (
    <Card
      size="small"
      title={
        <Space>
          <Typography.Text strong>
            {multiCompany ? "Departments (whole branch)" : "Departments"}
          </Typography.Text>
          <Typography.Text type="secondary">({items.length})</Typography.Text>
        </Space>
      }
      extra={
        <Space>
          <Input.Search
            placeholder="Search by name…"
            allowClear
            size="small"
            style={{ width: 200 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button
            type="primary"
            size="small"
            disabled={!canCreate || !props.companyUid}
            onClick={() => setAddOpen(true)}
          >
            + Add Department
          </Button>
        </Space>
      }
    >
      {showSkeleton ? (
        <Skeleton active paragraph={{ rows: 3 }} title={false} />
      ) : (
        <Table
          size="small"
          loading={loading}
          rowKey={(r) => r.uid}
          dataSource={items}
          pagination={items.length > 10 ? { pageSize: 10 } : false}
          locale={{ emptyText: "No departments yet." }}
          columns={[
            { title: "Name", dataIndex: "name", key: "name" },
            ...(multiCompany
              ? [
                  {
                    title: "Company",
                    dataIndex: "companyName",
                    key: "companyName",
                  },
                ]
              : []),
            { title: "Users", dataIndex: "usersCount", key: "usersCount", width: 80 },
            {
              title: "",
              key: "actions",
              width: 90,
              render: (_: any, record: Department) => (
                <Button
                  size="small"
                  type="link"
                  disabled={!canEdit}
                  onClick={() => setEditTarget(record)}
                >
                  Edit
                </Button>
              ),
            },
          ]}
        />
      )}
      <AddOrEditDepartmentDialog
        mode={editTarget ? "edit" : "add"}
        open={addOpen || !!editTarget}
        scopeCompanies={props.scopeCompanies}
        defaultCompanyUid={props.companyUid}
        existing={editTarget}
        onCancel={() => {
          setAddOpen(false);
          setEditTarget(null);
        }}
        onFinish={onMutated}
      />
    </Card>
  );
}

function AddOrEditDepartmentDialog(props: {
  mode: "add" | "edit";
  open: boolean;
  scopeCompanies: Company[];
  defaultCompanyUid: string | undefined;
  existing?: Department | null;
  onCancel: () => void;
  onFinish: () => void;
}) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (!props.open) {
      form.resetFields();
      setError("");
    } else if (props.mode === "edit" && props.existing) {
      form.setFieldsValue({ name: props.existing.name });
    } else if (props.mode === "add") {
      const def = props.scopeCompanies.find((c) => c.uid === props.defaultCompanyUid);
      if (def) form.setFieldsValue({ company: { display: def.name, value: def.uid } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  const companyRequest = (search: string) => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? props.scopeCompanies.filter((c) => c.name.toLowerCase().startsWith(q))
      : props.scopeCompanies;
    return Promise.resolve(
      filtered.map((c) => ({ display: c.name, value: c.uid, dataItem: c })),
    );
  };

  async function onSubmit(): Promise<void> {
    try {
      const values = await form.validateFields();
      setSaving(true);
      setError("");
      if (props.mode === "add") {
        await addCompanyDepartment({
          name: values.name,
          companyUid: values.company?.value,
        });
        message.success(
          `Department "${values.name}" created under "${values.company?.display}".`,
        );
      } else {
        await updateCompanyDepartment({
          uid: props.existing!.uid,
          rowVersion: props.existing!.rowVersion,
          name: values.name,
          companyUid: props.existing!.companyUid,
        });
        message.success(`Department renamed to "${values.name}".`);
      }
      setSaving(false);
      props.onFinish();
    } catch (err: any) {
      setSaving(false);
      if (err?.errorFields) return;
      setError(getProblemMessage(err));
    }
  }

  const title =
    props.mode === "add"
      ? "Add Department"
      : `Rename Department "${props.existing?.name ?? ""}"`;

  return (
    <Modal
      open={props.open}
      title={title}
      onCancel={props.onCancel}
      onOk={onSubmit}
      okText={props.mode === "add" ? "Create" : "Save"}
      confirmLoading={saving}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        {props.mode === "add" && (
          <Form.Item
            name="company"
            label="Register for (company)"
            rules={[{ required: true, message: "Company is required" }]}
          >
            <ComboBox request={companyRequest} />
          </Form.Item>
        )}
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: "Department name is required" }]}
        >
          <Input />
        </Form.Item>
      </Form>
      {error && (
        <Typography.Text type="danger" className="mt-2 block">
          {error}
        </Typography.Text>
      )}
    </Modal>
  );
}

export default CompanyDepartmentsPanel;
